import { zodResolver } from '@hookform/resolvers/zod';
import { MapPin } from 'lucide-react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';

import PostalCodeCity from '@/components/forms/postal-code-city';
import RequiredFieldsLegend from '@/components/forms/required-fields-legend';
import Text from '@/components/forms/text';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { localisationVehiculeSchema, type LocalisationVehiculeValues } from '@/schemas/localisation-vehicule';
import type { AccimotoPickupLocation } from '@/types/api/accimoto';

interface AccimotoPickupLocationFormProps {
    pickupLocation: AccimotoPickupLocation | null;
    loading: boolean;
    onSubmit: (form: LocalisationVehiculeValues) => void;
}

/** « Où se situe le véhicule ? » : fourrière ou épaviste où Accimoto viendra enlever le véhicule. */
const AccimotoPickupLocationForm = ({ pickupLocation, loading, onSubmit }: AccimotoPickupLocationFormProps) => {
    const methods = useForm<LocalisationVehiculeValues>({
        defaultValues: {
            name: pickupLocation?.name ?? '',
            address: pickupLocation?.address ?? '',
            postal_code: pickupLocation?.postal_code ?? '',
            town: pickupLocation?.town ?? '',
        },
        resolver: zodResolver(localisationVehiculeSchema) as unknown as Resolver<LocalisationVehiculeValues>,
    });

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = methods;
    const values = watch();

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Card>
                    <CardHeader className="pb-6 border-b">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-primary" />
                            Où se situe le véhicule ?
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-6">
                        <Text
                            type={'text'}
                            label={'Nom de la fourrière / épaviste'}
                            {...register('name')}
                            value={values.name}
                            error={errors.name}
                            maxLength={100}
                            required
                        />

                        <Text
                            type={'text'}
                            label={'Adresse'}
                            {...register('address')}
                            value={values.address}
                            error={errors.address}
                            maxLength={255}
                            required
                        />

                        <PostalCodeCity
                            label={'Code postal, ville (ex: 75011 PARIS)'}
                            cp="postal_code"
                            ville="town"
                            required
                            error={errors.postal_code?.message ?? errors.town?.message}
                        />

                        <RequiredFieldsLegend />

                        <Button
                            type="submit"
                            className="w-full cursor-pointer text-white"
                            loading={loading}
                            loadingText="Enregistrement en cours..."
                        >
                            Je valide le lieu du véhicule
                        </Button>
                    </CardContent>
                </Card>
            </form>
        </FormProvider>
    );
};

export default AccimotoPickupLocationForm;
